import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { BookOpen } from "lucide-react";
import api from "@/lib/api";
import Seo, { orgSchema } from "@/components/Seo";

const SITE = "https://signiastylettoix.com";

export default function LearnHub() {
    const [data, setData] = useState(null);
    const [params, setParams] = useSearchParams();
    const cat = params.get("category") || "";

    useEffect(() => {
        api.get("/learn/articles").then((r) => setData(r.data));
    }, []);

    if (!data) return <div className="min-h-[60vh] flex items-center justify-center text-[#4a5568]">Loading…</div>;
    const articles = cat ? data.articles.filter((a) => a.category === cat) : data.articles.filter((a) => a.category !== "glossary");
    const glossary = data.articles.filter((a) => a.category === "glossary");

    return (
        <>
            <Seo
                title="Hearing Aid Knowledge Centre — India's Signia Encyclopedia"
                description="67+ clinically-reviewed articles on hearing loss, Signia technology, buying guides, maintenance and troubleshooting — India's most complete Signia knowledge base."
                canonical={`${SITE}/learn`}
                schema={{ "@context": "https://schema.org", "@graph": [orgSchema()] }}
            />
            <section className="pt-12 md:pt-16 pb-16">
                <div className="container-x">
                    <p className="eyebrow inline-flex items-center gap-2"><BookOpen size={12} /> Knowledge Centre</p>
                    <h1 className="font-serif text-[36px] md:text-[52px] leading-[1.08] mt-4 max-w-[860px]" data-testid="learn-h1">
                        Everything About Hearing Loss & Signia, In One Place
                    </h1>
                    {!cat && data.paths?.length > 0 && (
                        <div className="mt-8" data-testid="learning-paths">
                            <p className="eyebrow">Guided learning paths</p>
                            <div className="mt-4 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {data.paths.map((p) => (
                                    <Link key={p.slug} to={`/learn/paths/${p.slug}`} className="block bg-[#003E4D] text-white rounded-2xl p-5 hover:bg-[#0F6B7D] transition">
                                        <h2 className="font-serif text-[18px] leading-snug">{p.title}</h2>
                                        <p className="text-[13px] text-[#cfe3e8] mt-2">{p.description}</p>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}
                    <div className="mt-8 flex flex-wrap gap-2" data-testid="learn-categories">
                        <button onClick={() => setParams({})} className={`text-[13px] rounded-full px-4 py-2 border ${!cat ? "bg-[#003E4D] text-white border-[#003E4D]" : "bg-white border-[#e2ddd4] text-[#003E4D]"}`}>All</button>
                        {data.categories.map((c) => (
                            <button key={c.slug} onClick={() => setParams({ category: c.slug })} className={`text-[13px] rounded-full px-4 py-2 border ${cat === c.slug ? "bg-[#003E4D] text-white border-[#003E4D]" : "bg-white border-[#e2ddd4] text-[#003E4D]"}`}>{c.name}</button>
                        ))}
                    </div>
                    <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="learn-grid">
                        {articles.map((a) => (
                            <Link key={a.slug} to={`/learn/${a.slug}`} className="block bg-white border border-[#e2ddd4] rounded-2xl p-6 hover:border-[#003E4D] transition-colors">
                                <div className="text-[11px] uppercase tracking-wider text-[#B8860B]">{data.categories.find((c) => c.slug === a.category)?.name} · {a.read_minutes} min</div>
                                <h2 className="font-serif text-[19px] leading-snug mt-2 text-[#1a1a1a]">{a.title}</h2>
                                <p className="text-[13.5px] text-[#4a5568] mt-2 line-clamp-3">{a.meta_description}</p>
                            </Link>
                        ))}
                    </div>
                    {!cat && (
                        <>
                            <h2 className="font-serif text-[26px] text-[#003E4D] mt-14 mb-4">Glossary</h2>
                            <div className="flex flex-wrap gap-2">
                                {glossary.map((g) => (
                                    <Link key={g.slug} to={`/learn/${g.slug}`} className="text-[13px] bg-white border border-[#e2ddd4] rounded-full px-4 py-2 text-[#003E4D] hover:border-[#003E4D]">{g.title.replace(" — Hearing Aid Glossary", "")}</Link>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </section>
        </>
    );
}
